import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { CmsCategoryIcon } from '@/components/painel/cms/cms-icons';
import { Icon } from '@/components/icons/Icon';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import {
  guidePremiumCardClassName,
  resolveGuideCardTone,
  type GuideCardTone,
} from '@/lib/guide-card-tones';
import { isLibraryIconRef, libraryIconSlug } from '@/lib/icon-library';
import { cn } from '@/lib/utils';

const WATERMARK_ICON_CLASS =
  'h-[4.5rem] w-[4.5rem] opacity-[0.92] transition-transform duration-300 group-hover:scale-105 md:h-20 md:w-20';

type GuidePremiumCardShellProps = {
  colorKey?: string | null;
  /** Nome do ícone CMS (Lucide / máscara). */
  icon?: string | null;
  /** SVG estático em /public (ex.: `/icons/user.svg`). Tem prioridade sobre `icon`. */
  iconSrc?: string | null;
  title: string;
  /** Badge superior (categoria, “Popular”, etc.). */
  badge?: ReactNode;
  /** Linha inferior (views, data, …). */
  footer?: ReactNode;
  /** Mostrar seta à direita no footer. */
  showArrow?: boolean;
  className?: string;
  children?: ReactNode;
};

export function GuidePremiumCardShell({
  colorKey,
  icon,
  iconSrc,
  title,
  badge,
  footer,
  showArrow = true,
  className,
  children,
}: GuidePremiumCardShellProps) {
  const tone: GuideCardTone = resolveGuideCardTone(colorKey);
  const iconName = icon?.trim() || 'FileText';

  return (
    <div className={guidePremiumCardClassName(tone, className)}>
      <span
        className={cn(
          'pointer-events-none absolute -right-2 -top-2 flex h-24 w-24 items-center justify-center md:h-28 md:w-28',
          'transition-colors duration-300',
          tone.watermark
        )}
        aria-hidden
      >
        {iconSrc ? (
          <MaskedSvgIcon src={iconSrc} className={WATERMARK_ICON_CLASS} />
        ) : isLibraryIconRef(iconName) && libraryIconSlug(iconName) ? (
          <Icon name={libraryIconSlug(iconName)!} className={WATERMARK_ICON_CLASS} />
        ) : (
          <CmsCategoryIcon name={iconName} className={WATERMARK_ICON_CLASS} />
        )}
      </span>

      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        {badge ? <div className="mb-3 flex flex-wrap items-center gap-1.5">{badge}</div> : null}

        <h3
          className={cn(
            'mb-5 line-clamp-3 max-w-[85%] font-poppins text-base font-bold leading-snug text-gray-900 transition-colors duration-300 md:text-[17px]',
            tone.titleHover
          )}
        >
          {title}
        </h3>

        {children}

        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <div className="min-w-0 font-poppins text-sm text-gray-400">{footer}</div>
          {showArrow ? (
            <span
              className={cn(
                'inline-flex shrink-0 items-center text-gray-400',
                'transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-gray-600'
              )}
            >
              <ArrowRight className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function GuidePremiumBadge({
  colorKey,
  children,
  className,
}: {
  colorKey?: string | null;
  children: ReactNode;
  className?: string;
}) {
  const tone = resolveGuideCardTone(colorKey);
  return (
    <span
      className={cn(
        'inline-flex w-fit rounded-full px-2.5 py-1 font-poppins text-[10px] font-semibold leading-none',
        tone.badge,
        className
      )}
    >
      {children}
    </span>
  );
}
